import React, {memo, useCallback} from 'react';

import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  LetterAvatar,
} from '@/app/shared-components';
import {ISplitParticipant} from '@/dataSource/src/api/splits/entities';

import {FdicIcon} from './FdicIcon';

interface SplitsSelectMemberItemProps {
  participant: ISplitParticipant;
  isSelected: boolean;
  onSelect: (participant: ISplitParticipant) => void;
}

const SplitsSelectMemberItem = memo(
  ({participant, isSelected, onSelect}: SplitsSelectMemberItemProps) => {
    const isPaid = participant.payment_confirmed;

    const handleClick = useCallback(() => {
      if (!isPaid) {
        onSelect(participant);
      }
    }, [isPaid, onSelect, participant]);

    return (
      <Box
        component="button"
        className={`splits-select-member-item ${isSelected ? 'selected' : ''} ${isPaid ? 'paid' : ''}`}
        onClick={handleClick}
        disabled={isPaid}>
        {isSelected ? (
          <RadioButtonCheckedIcon className="splits-select-member-radio selected" />
        ) : (
          <RadioButtonUncheckedIcon className="splits-select-member-radio" />
        )}
        <LetterAvatar
          size={40}
          name={participant.name}
        />
        <Typography className="splits-select-member-name">
          {participant.name}
        </Typography>
      </Box>
    );
  },
);

interface SplitsSelectParticipantCardProps {
  participants: ISplitParticipant[];
  selectedParticipant: ISplitParticipant | null;
  onSelectParticipant: (participant: ISplitParticipant) => void;
  onContinueFromSelectParticipant: () => void;
  onGetAvatarImage: () => React.ReactNode;
}

export function SplitsSelectMemberCard({
  participants,
  selectedParticipant,
  onSelectParticipant,
  onContinueFromSelectParticipant,
  onGetAvatarImage,
}: SplitsSelectParticipantCardProps) {
  const {t} = useTranslation();

  return (
    <Box className="splits-landing-card splits-landing-card-select-member">
      <Box
        component="div"
        className="splits-landing-card-fdic">
        <FdicIcon />
      </Box>
      <Box className="splits-landing-card-select-member-body">
        {onGetAvatarImage()}
        <Typography
          className="splits-landing-headline"
          component="h1">
          {t('split_bills_page.landing.select_member_title')}
        </Typography>
        <>
          <Box className="splits-select-member-list">
            {participants.map(participant => (
              <SplitsSelectMemberItem
                key={participant.id}
                participant={participant}
                isSelected={selectedParticipant?.id === participant.id}
                onSelect={onSelectParticipant}
              />
            ))}
          </Box>

          <AppButton
            id="splits-select-member-continue"
            data-testid="splits-select-member-continue"
            onClick={onContinueFromSelectParticipant}
            label={t('split_bills_page.landing.continue_to_split')}
            variant={AppButtonVariant.onPrimary}
            disabled={!selectedParticipant}
          />
        </>
      </Box>
    </Box>
  );
}
